/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import PressPage from './pages/PressPage';

// ... existing imports
import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import NavBar from './components/NavBar';
import { WardrobePanel } from './components/WardrobePanel';
import OutfitStack from './components/OutfitStack';
import ModelGenerationScreen from './components/StartScreen';
import OutfitNameInputModal from './components/OutfitNameInputModal';
import ShareOutfitModal from './components/ShareOutfitModal';
import LookbookCreatorModal from './components/LookbookCreatorModal';
import ColorPickerPanel from './components/ColorPickerPanel';
import StyleTipsModal from './components/StyleTipsModal';
import VoucherPurchaseModal from './components/VoucherPurchaseModal';
import PasswordPromptModal from './components/PasswordPromptModal';
import OutfitVariationsModal from './components/OutfitVariationsModal';
import ShoppingBasketModal from './components/ShoppingBasketModal';
import { AgentingWorkflowChat } from './pages/AgentingWorkflowPage';
import TrendSpotter from './components/TrendSpotter';
import OnboardingTooltip from './components/OnboardingTooltip';
import AuthModal from './components/AuthModal';
import { ShoppingAssistantChat } from './components/ShoppingAssistant/ShoppingAssistantChat';
import { defaultWardrobe, predefinedAccessorySets } from '@shared/data/wardrobe';
import {
    WardrobeItem, OutfitLayer, UserProfile, SavedOutfit, SavedContent,
    AppPage, ItemAdjustment, SceneConfiguration, RetailItem, AccessorySet,
    StyleReport, Lookbook, BookingDetails
} from '@shared/types';
import {
    generateOutfitImage, findSimilarItems, getFashionAdvice, performImageEdit,
    generateComprehensiveReport, analyzeColorAndHair, analyzeFitConfidence, analyzeFitMap,
    generateAttitudeNote
} from '@shared/services/geminiService';
import {
    getUserProfile, saveUserProfile, saveUserSavedOutfits,
    saveUserLookbooks, saveUserSavedContent,
    checkGenerationQuota, incrementGenerationCount, toggleUserFavoritePose,
    hasMembershipAccess, hasAssistantAccess
} from '@shared/services/userService';
import { POSE_CATEGORIES, MEMBERSHIP_LIMITS, VIEW_360_POSES } from '@shared/constants';
import { getAppConfiguration, submitBooking, authenticateUser } from '@shared/services/mockBackend';
import { submitVoucherPurchase } from '@shared/services/paymentService';
import { Logger } from '@shared/services/logger';
import { portfolioItems } from '@shared/data/portfolioData';
import { useTranslation } from './i18n/useTranslation';
import { useAppContext } from './AppContext';
import { useOutfit } from './hooks/useOutfit';
import { AppRoutes } from './AppRoutes';

const App: React.FC = () => {
    const { t, lang } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const {
        currentUserId, setCurrentUserId, allUsers, setAllUsers, userProfile, setUserProfile,
        wardrobe, setWardrobe, savedOutfits, setSavedOutfits,
        lookbooks, setLookbooks, savedContent, setSavedContent,
        isDarkMode, setIsDarkMode
    } = useAppContext();

    const {
        currentLayer, updateLayerWithHistory, resetOutfit, currentOutfitId, setCurrentOutfitId,
        baseModelImageUrl, setBaseModelImageUrl, handleUndo, handleRedo
    } = useOutfit();

    const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [loadingAction, setLoadingAction] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [basket, setBasket] = useState<RetailItem[]>([]);
    const [userImageFile, setUserImageFile] = useState<File | null>(null);
    const [moodboardInjectionItem, setMoodboardInjectionItem] = useState<{ item: WardrobeItem; timestamp: number } | null>(null);

    const handleNavigate = useCallback((page: AppPage, params?: Record<string, string>) => {
        const queryParams = params ? '?' + new URLSearchParams(params).toString() : '';
        navigate(`/${page}${queryParams}`);
    }, [navigate]);

    const currentPage = (location.pathname.split('/')[1] || 'home') as AppPage;
    const pageParams = Object.fromEntries(new URLSearchParams(location.search));

    const [showOnboarding, setShowOnboarding] = useState(false);
    const [onboardingStep, setOnboardingStep] = useState(0);

    const [showStartScreen, setShowStartScreen] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showLookbookModal, setShowLookbookModal] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showStyleTipsModal, setShowStyleTipsModal] = useState(false);
    const [showAgentChat, setShowAgentChat] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [colorPickerTarget, setColorPickerTarget] = useState<{ type: 'garment' | 'accessory', id: string } | null>(null);

    const [showVoucherModal, setShowVoucherModal] = useState(false);
    const [showVariationsModal, setShowVariationsModal] = useState(false);
    const [showBasketModal, setShowBasketModal] = useState(false);
    const [showShoppingAssistant, setShowShoppingAssistant] = useState(false);
    const [showTrendSpotter, setShowTrendSpotter] = useState(false);
    const [pendingUserId, setPendingUserId] = useState<string | null>(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    const [searchResults, setSearchResults] = useState<RetailItem[]>([]);
    const [searchGrounding, setSearchGrounding] = useState<any>(null);
    const [assistantQuery, setAssistantQuery] = useState<string | null>(null);
    const [styleTipsContent, setStyleTipsContent] = useState<string | null>(null);
    const [styleTipsType, setStyleTipsType] = useState<'tips' | 'color_analysis' | 'plan'>('tips');
    const [styleReport, setStyleReport] = useState<StyleReport | null>(null);
    const [outfitVariations, setOutfitVariations] = useState<string | null>(null);
    const [adminAccessorySets, setAdminAccessorySets] = useState<AccessorySet[]>([]);

    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }

        try {
            const sets = JSON.parse(localStorage.getItem('admin_curated_sets') || '[]');
            setAdminAccessorySets(sets);
        } catch (e) {
            console.error(e);
        }
    }, [isDarkMode]);

    const handleUserChangeRequest = async (newUserId: string) => {
        if (newUserId.toLowerCase().includes('admin')) {
            setPendingUserId(newUserId);
            setShowPasswordModal(true);
        } else {
            setCurrentUserId(newUserId);
        }
    };

    const handlePasswordSubmit = async (password: string) => {
        if (!pendingUserId) return false;
        try {
            const result = await authenticateUser(pendingUserId, password);
            if (result.success) {
                setCurrentUserId(pendingUserId);
                setShowPasswordModal(false);
                setPendingUserId(null);
                return true;
            } else {
                return false;
            }
        } catch (e) {
            Logger.error('Auth error', e);
            return false;
        }
    };

    const handlePhotoUpload = async (file: File) => {
        Logger.info('User uploaded photo for model generation.');
        setUserImageFile(file);
        setIsLoading(true);
        setLoadingAction('model');
        setLoadingMessage(t('loading_generating_model'));
        setError(null);

        try {
            const { generateModelImage, analyzeUserImageForAttributes } = await import('./services/geminiService');
            const qualityPreference = userProfile.saveImageQuality || 'medium';

            // Generate model and analyze in parallel
            const [modelUrl, detectedProfile] = await Promise.all([
                generateModelImage(file, qualityPreference),
                analyzeUserImageForAttributes(file).catch(() => undefined)
            ]);

            // Finalize model
            handleModelFinalized(modelUrl, detectedProfile);
            Logger.info('Auto model generation successful');
        } catch (err) {
            const details = err instanceof Error ? err.message : String(err);
            Logger.error('Auto model generation failed', err);
            setError(t('error_model_generation', { details }));
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
            setLoadingAction(null);
        }
    };


    const handleModelFinalized = (modelUrl: string, detectedProfile?: Partial<UserProfile>) => {
        Logger.info('Model generation finalized.');
        setBaseModelImageUrl(modelUrl);
        if (detectedProfile) {
            const updatedProfile = { ...userProfile, ...detectedProfile };
            setUserProfile(updatedProfile);
            saveUserProfile(currentUserId, updatedProfile).catch(e => Logger.error('Failed to save profile', e));
        }

        // --- UPLOAD TO WARDROBE (IF LOGGED IN) ---
        if (!currentUserId.includes('guest')) {
            const newWardrobeItem: WardrobeItem = {
                id: `uploaded-${Date.now()}`,
                nameKey: 'wardrobe_item_uploaded_name',
                url: modelUrl,
                type: 'garment',
                category: 'Outerwear', // Default category for uploaded models
                brand: t('brand_custom'),
                material: t('material_custom')
            };
            setWardrobe(prev => [newWardrobeItem, ...prev]);
            // Persistence is handled by the useEffect in AppContext.tsx
            Logger.info('Auto-added generated model to wardrobe.');
        }
        // -----------------------------------------

        setShowStartScreen(false);
        handleNavigate('canvas');
        const hasSeenOnboarding = localStorage.getItem(`onboarding_completed_${currentUserId}`);
        if (!hasSeenOnboarding) {
            setIsLeftPanelOpen(true);
            setIsRightPanelOpen(true);
            setShowOnboarding(true);
            setOnboardingStep(1);
        }
    };

    const handleRestartOnboarding = () => {
        setIsLeftPanelOpen(true);
        setIsRightPanelOpen(true);
        if (currentPage !== 'canvas') {
            handleNavigate('canvas');
        }
        setShowOnboarding(true);
        setOnboardingStep(1);
    };

    const handleOnboardingNext = () => {
        const nextStep = onboardingStep + 1;
        if (nextStep > 4) {
            setShowOnboarding(false);
            localStorage.setItem(`onboarding_completed_${currentUserId}`, 'true');
        } else {
            setOnboardingStep(nextStep);
        }
    };

    const handleOnboardingSkip = () => {
        setShowOnboarding(false);
        localStorage.setItem(`onboarding_completed_${currentUserId}`, 'true');
    };

    const getOnboardingStepData = (step: number) => {
        switch (step) {
            case 1: return { id: 'onboarding-wardrobe-panel', title: t('onboarding_step1_title'), content: t('onboarding_step1_content') };
            case 2: return { id: 'onboarding-canvas-container', title: t('onboarding_step2_title'), content: t('onboarding_step2_content') };
            case 3: return { id: 'onboarding-outfit-stack', title: t('onboarding_step3_title'), content: t('onboarding_step3_content') };
            case 4: return { id: 'onboarding-pose-bar', title: t('onboarding_step4_title'), content: t('onboarding_step4_content') };
            default: return { id: '', title: '', content: '' };
        }
    };

    const triggerGeneration = async (layer: OutfitLayer, actionType: 'item' | 'pose' | 'background' = 'item') => {
        if (!baseModelImageUrl) return;
        if (!checkGenerationQuota(userProfile)) {
            const level = (userProfile.membershipLevel?.toLowerCase() || 'basic') as keyof typeof MEMBERSHIP_LIMITS;
            const limitMsg = t('error_quota_exceeded', { limit: String(MEMBERSHIP_LIMITS[level]) });
            Logger.warn(`Generation quota exceeded for user ${currentUserId}`);
            setError(limitMsg);
            return;
        }
        setIsLoading(true);
        setLoadingAction(actionType);
        let msg = t(`loading_updating_${actionType}` as any);
        if (actionType === 'pose') {
            const allPoses = POSE_CATEGORIES.flatMap((c: any) => c.poses);
            const targetPose = allPoses.find((p: any) => p.instruction === layer.currentPoseInstruction);
            const poseName = targetPose ? t(targetPose.labelKey as any) : t('common_pose');
            msg = t('loading_updating_pose', { poseName });
        }
        setLoadingMessage(msg);
        setError(null);
        try {
            const accessoriesArg = layer.accessories.map(acc => ({
                item: acc,
                color: layer.appliedAccessoryColors[acc.id]
            }));
            const generatedImage = await generateOutfitImage(
                baseModelImageUrl,
                layer.currentPoseInstruction,
                layer.garments,
                accessoriesArg,
                Object.values(layer.appliedGarmentColors).join(', '),
                undefined,
                layer.itemAdjustments,
                layer.sceneConfig,
                userProfile.saveImageQuality,
                userProfile.measurements
            );
            const updatedProfile = await incrementGenerationCount(currentUserId);
            setUserProfile(updatedProfile);
            const previousOutputs = (actionType === 'pose') ? layer.poseOutputs : (actionType === 'background' ? { [layer.currentPoseInstruction]: generatedImage } : {});
            const newLayer = {
                ...layer,
                poseOutputs: { ...previousOutputs, [layer.currentPoseInstruction]: generatedImage }
            };
            updateLayerWithHistory(newLayer);
            Logger.info(`Outfit generation successful (${actionType})`);
        } catch (err) {
            const details = err instanceof Error ? err.message : String(err);
            Logger.error('Outfit generation failed', err);
            setError(t('error_update_outfit', { details }));
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
            setLoadingAction(null);
        }
    };

    const handleGenerate3DViews = async () => {
        if (!baseModelImageUrl) return false;
        if (!checkGenerationQuota(userProfile)) {
            const level = (userProfile.membershipLevel?.toLowerCase() || 'basic') as keyof typeof MEMBERSHIP_LIMITS;
            setError(t('error_quota_exceeded', { limit: String(MEMBERSHIP_LIMITS[level]) }));
            return false;
        }
        setIsLoading(true);
        setLoadingAction('3d');
        setLoadingMessage(t('loading_updating_3d', { current: 1 }));
        setError(null);
        try {
            const missingPoses = VIEW_360_POSES.filter(pose => !currentLayer.poseOutputs[pose]);
            if (missingPoses.length === 0) {
                setIsLoading(false);
                setLoadingAction(null);
                return true;
            }
            let updatedOutputs = { ...currentLayer.poseOutputs };
            let successfulGens = 0;
            let failureCount = 0;
            const totalToGen = missingPoses.length;

            for (let i = 0; i < totalToGen; i++) {
                const pose = missingPoses[i];
                const currentStep = Object.keys(updatedOutputs).length % 4 + 1;
                setLoadingMessage(t('loading_updating_3d', { current: currentStep }));

                try {
                    const accessoriesArg = currentLayer.accessories.map(acc => ({
                        item: acc,
                        color: currentLayer.appliedAccessoryColors[acc.id]
                    }));
                    const generatedImage = await generateOutfitImage(
                        baseModelImageUrl,
                        pose,
                        currentLayer.garments,
                        accessoriesArg,
                        Object.values(currentLayer.appliedGarmentColors).join(', '),
                        undefined,
                        currentLayer.itemAdjustments,
                        currentLayer.sceneConfig,
                        userProfile.saveImageQuality,
                        userProfile.measurements
                    );
                    updatedOutputs[pose] = generatedImage;
                    successfulGens++;
                } catch (poseErr) {
                    Logger.error(`3D generation failed for pose: ${pose}`, poseErr);
                    failureCount++;
                }
            }

            if (successfulGens > 0) {
                const updatedProfile = await incrementGenerationCount(currentUserId);
                setUserProfile(updatedProfile);
            }

            const newLayer = { ...currentLayer, poseOutputs: updatedOutputs };
            updateLayerWithHistory(newLayer);

            if (failureCount > 0) {
                const errorMsg = successfulGens > 0
                    ? t('error_3d_partial_success', { success: successfulGens, total: totalToGen })
                    : t('error_3d_total_failure');
                setError(errorMsg);
                return successfulGens > 0;
            }

            Logger.info('3D view generation successful');
            return true;
        } catch (err) {
            Logger.error('3D view generation failed', err);
            setError(t('error_generic_short'));
            return false;
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
            setLoadingAction(null);
        }
    };

    const handleItemSelect = async (file: File | null, itemInfo: WardrobeItem, initialAdjustments?: ItemAdjustment) => {
        // Intercept for moodboard mode
        if (currentPage === 'style-lab' && pageParams.mode === 'moodboard') {
            setMoodboardInjectionItem({ item: itemInfo, timestamp: Date.now() });
            return;
        }

        let newLayer = { ...currentLayer };
        // Safety check for sceneConfig
        if (!newLayer.sceneConfig) {
            newLayer.sceneConfig = {
                cameraView: 'full-body',
                backgroundPrompt: 'neutral light gray studio backdrop',
                modelPosition: { x: 0, y: 0 },
                modelScale: 1
            };
        }
        const itemWithFile = file ? { ...itemInfo, originalFile: file } : itemInfo;
        if (itemInfo.type === 'garment') {
            const category = itemInfo.category;
            // Stack garments: replace same category, keep others
            const otherGarments = (newLayer.garments || []).filter(g => g.category !== category);
            newLayer.garments = [...otherGarments, itemWithFile];
            newLayer.appliedGarmentColors[itemInfo.id] = itemInfo.defaultColor || '';
            // Sync mainGarment for compatibility
            newLayer.mainGarment = itemWithFile;
            newLayer.appliedMainGarmentColor = itemInfo.defaultColor || '';
        } else {
            if (!newLayer.accessories.some(a => a.id === itemInfo.id)) {
                newLayer.accessories = [...newLayer.accessories, itemWithFile];
                newLayer.appliedAccessoryColors[itemInfo.id] = itemInfo.defaultColor || '';
            }
        }
        const adjustments = initialAdjustments || itemInfo.defaultAdjustments;
        if (adjustments) {
            newLayer.itemAdjustments = { ...newLayer.itemAdjustments, [itemInfo.id]: adjustments };
        }
        updateLayerWithHistory(newLayer);
        await triggerGeneration(newLayer, 'item');
    };

    const handleRemoveGarment = (garmentId: string) => {
        const newGarments = (currentLayer.garments || []).filter(g => g.id !== garmentId);
        const newLayer = {
            ...currentLayer,
            garments: newGarments,
            appliedGarmentColors: { ...currentLayer.appliedGarmentColors }
        };

        // Sync mainGarment to the last added garment, or undefined
        if (newGarments.length > 0) {
            newLayer.mainGarment = newGarments[newGarments.length - 1];
            newLayer.appliedMainGarmentColor = newLayer.appliedGarmentColors[newLayer.mainGarment.id];
        } else {
            newLayer.mainGarment = undefined;
            newLayer.appliedMainGarmentColor = undefined;
        }

        delete newLayer.appliedGarmentColors[garmentId];
        updateLayerWithHistory(newLayer);
        triggerGeneration(newLayer, 'item');
    };


    const handleApplyAccessorySet = (set: AccessorySet) => {
        // Handle Accessories
        const newAccessories = set.accessoryIds.map(id => wardrobe.find(i => i.id === id)).filter((i): i is WardrobeItem => !!i);

        // Handle Garments (if any)
        let newGarments = [...currentLayer.garments];
        const setGarments = (set.garmentIds || []).map(id => wardrobe.find(i => i.id === id)).filter((i): i is WardrobeItem => !!i);

        if (setGarments.length > 0) {
            setGarments.forEach(g => {
                // Replace same category
                newGarments = newGarments.filter(existing => existing.category !== g.category);
                newGarments.push(g);
            });
        }

        const newLayer = { ...currentLayer, accessories: newAccessories, garments: newGarments };

        // Colors
        newAccessories.forEach(acc => {
            newLayer.appliedAccessoryColors[acc.id] = acc.defaultColor || '';
        });
        setGarments.forEach(g => {
            newLayer.appliedGarmentColors[g.id] = g.defaultColor || '';
        });

        updateLayerWithHistory(newLayer);
        triggerGeneration(newLayer, 'item');
    };

    const handlePoseSelect = async (poseInstruction: string) => {
        const newLayer = { ...currentLayer, currentPoseInstruction: poseInstruction };
        if (!newLayer.poseOutputs[poseInstruction]) {
            await triggerGeneration(newLayer, 'pose');
        } else {
            updateLayerWithHistory(newLayer);
        }
    };

    const handleToggleFavoritePose = async (poseInstruction: string) => {
        const updatedProfile = await toggleUserFavoritePose(currentUserId, poseInstruction);
        setUserProfile(updatedProfile);
    };

    const handleSceneUpdate = (config: SceneConfiguration) => {
        const newLayer = { ...currentLayer, sceneConfig: config };
        updateLayerWithHistory(newLayer);
        triggerGeneration(newLayer, 'background');
    };

    const handleColorChange = (color: string) => {
        if (colorPickerTarget) {
            const newLayer = { ...currentLayer };
            if (colorPickerTarget.type === 'garment') {
                newLayer.appliedGarmentColors[colorPickerTarget.id] = color;
            } else {
                newLayer.appliedAccessoryColors[colorPickerTarget.id] = color;
            }
            updateLayerWithHistory(newLayer);
            triggerGeneration(newLayer, 'item');
        }
    };

    const handleSaveOutfit = async (name: string, description: string, tags: string[], saveAsNew: boolean, poseNote: string) => {
        setIsLoading(true);
        try {
            const currentPose = currentLayer.currentPoseInstruction;
            const updatedLayer = {
                ...currentLayer,
                poseDescriptions: { ...(currentLayer.poseDescriptions || {}), [currentPose]: poseNote }
            };
            // Note: We don't call updateLayerWithHistory here to avoid cluttering history with just a note change
            // we will use the updatedLayer directly for saving
            const outfitId = saveAsNew ? `outfit-${Date.now()}` : (currentOutfitId || `outfit-${Date.now()}`);
            const newOutfit: SavedOutfit = {
                id: outfitId,
                name,
                description,
                tags,
                previewImageUrl: updatedLayer.poseOutputs[currentPose] || baseModelImageUrl || '',
                layer: updatedLayer,
                baseModelImageUrl: baseModelImageUrl || ''
            };
            const newOutfits = saveAsNew
                ? [...savedOutfits, newOutfit]
                : [...savedOutfits.filter(o => o.id !== newOutfit.id), newOutfit];
            await saveUserSavedOutfits(currentUserId, newOutfits);
            setSavedOutfits(newOutfits);
            setCurrentOutfitId(outfitId);
            setShowSaveModal(false);
            Logger.info(`Outfit saved: ${name}`);
        } catch (e) {
            const details = e instanceof Error ? e.message : String(e);
            Logger.error('Failed to save outfit', e);
            setError(t('error_save_outfit', { details }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleShareContent = (content: Omit<SavedContent, 'id' | 'createdAt'>) => {
        const newContent: SavedContent = {
            ...content,
            id: `content-${Date.now()}`,
            createdAt: new Date().toISOString()
        };
        const updatedContent = [...savedContent, newContent];
        setSavedContent(updatedContent);
        saveUserSavedContent(currentUserId, updatedContent).catch(e => Logger.error('Failed to save content', e));
    };

    const handleSuggestVariations = async () => {
        setIsLoading(true);
        setOutfitVariations(null);
        setShowVariationsModal(true);
        try {
            const garments = currentLayer.garments.length > 0
                ? `Garments: ${currentLayer.garments.map(g => `${t(g.nameKey)} (${currentLayer.appliedGarmentColors[g.id] || 'default'})`).join(', ')}`
                : 'No main garments';
            const accessories = currentLayer.accessories.length > 0
                ? `Accessories: ${currentLayer.accessories.map(a => `${t(a.nameKey)} (${currentLayer.appliedAccessoryColors[a.id] || 'default'})`).join(', ')}`
                : 'No accessories';

            const description = `${garments}. ${accessories}. Pose: ${currentLayer.currentPoseInstruction}.`;
            const advice = await getFashionAdvice("Suggest 3 distinct variations of this outfit for different occasions. Be specific about colors and layering.", description, (lang as any), userProfile);
            setOutfitVariations(advice);
        } catch (e) {
            setError(t('error_generic_short'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnalyzeOutfit = async () => {
        const currentPose = currentLayer.currentPoseInstruction;
        const displayImageUrl = currentLayer.poseOutputs[currentPose];
        if (!displayImageUrl) return;

        setIsLoading(true);
        setStyleReport(null);
        try {
            const report = await generateComprehensiveReport(displayImageUrl, userProfile, lang);
            setStyleReport(report);
        } catch (e) {
            Logger.error('Failed to analyze outfit', e);
            setError(t('error_generic_short'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnalyzeColor = async () => {
        const currentPose = currentLayer.currentPoseInstruction;
        const displayImageUrl = currentLayer.poseOutputs[currentPose];
        if (!displayImageUrl) return;

        setIsLoading(true);
        setStyleTipsContent(null);
        setStyleTipsType('color_analysis');
        setShowStyleTipsModal(true);

        try {
            const result = await analyzeColorAndHair(displayImageUrl, (lang as any));

            // Format color analysis as readable HTML/Markdown
            const content = `
### ${t('report_color_harmony', 'Color Season Analysis')}
**${t('color_season', 'Season')}:** ${result.season}

**${t('characteristics', 'Your Characteristics')}:**
- ${t('skin_tone', 'Skin Tone')}: ${result.characteristics.skinTone}
- ${t('hair_color', 'Hair Color')}: ${result.characteristics.hairColor}
- ${t('eye_color', 'Eye Color')}: ${result.characteristics.eyeColor}

### ${t('styling_advice', 'Styling Advice')}
**Clothing:** ${result.advice.clothing}
**Hair:** ${result.advice.hair}
**Makeup:** ${result.advice.makeup}
**Jewelry:** ${result.advice.jewelry}

### ${t('color_palette', 'Recommended Palette')}
- **Power Colors:** ${result.palette.powerColors.join(', ')}
- **Neutrals:** ${result.palette.neutrals.join(', ')}
- **Avoid:** ${result.palette.avoidColors.join(', ')}
            `.trim();

            setStyleTipsContent(content);
        } catch (e) {
            Logger.error('Failed to analyze color', e);
            setError(t('error_generic_short'));
            setShowStyleTipsModal(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFindSimilar = async (item: WardrobeItem) => {
        handleNavigate('styling-advisor', { section: 'shopping_assistant', query: `Find items similar to this ${t(item.nameKey)}` });
    };

    const handleRefineSearch = async (item: WardrobeItem, filters: any) => {
        handleNavigate('styling-advisor', { section: 'shopping_assistant', query: `Find items similar to this ${t(item.nameKey)} but with these details: ${JSON.stringify(filters)}` });
    };

    const handleSaveSearch = (results: RetailItem[], query: string) => {
        const content = results.map(r => `• **${r.name}** at ${r.retailer} (${r.price})\n  ${r.matchReason || ''}`).join('\n\n');
        handleShareContent({
            type: 'search',
            title: t('retailSearchModal_titleForGarment', { garmentName: query }),
            content: content
        });
        Logger.info(`Search results saved for query: ${query}`);
    };

    const handleSaveStyleReport = (report: StyleReport, imageUrl: string) => {
        const content = `
<div class="style-report-save">
  <p>${report?.summary || 'Style analysis complete.'}</p>
  <div style="margin: 20px 0; padding: 15px; background: rgba(79, 70, 229, 0.1); border-radius: 12px; border-left: 4px solid #4f46e5;">
    <h4 style="margin: 0; color: #4f46e5;">Score: ${report?.score || 0}/100</h4>
  </div>

  <h3 style="border-bottom: 1px solid #eee; padding-bottom: 5px;">${t('report_color_harmony')}</h3>
  <p><strong>${report?.colorAnalysis?.season || 'Season'}</strong></p>
  <p>${report?.colorAnalysis?.colorHarmony || ''}</p>
  <ul>
    ${(report?.colorAnalysis?.recommendations || []).map((r: string) => `<li>${r}</li>`).join('')}
  </ul>

  <h3 style="border-bottom: 1px solid #eee; padding-bottom: 5px;">${t('report_agent_reviews')}</h3>
  <div style="display: grid; grid-template-columns: 1fr; gap: 10px;">
    ${(report?.agentReviews || []).map((agent: any) => `
      <div style="padding: 10px; border: 1px solid #eee; border-radius: 8px;">
        <strong>${agent.agentName} (${agent.rating}/5)</strong>
        <p style="font-size: 0.9em; font-style: italic; margin-top: 5px;">"${agent.feedback}"</p>
      </div>
    `).join('')}
  </div>

  <h3 style="border-bottom: 1px solid #eee; padding-bottom: 5px;">${t('report_fit_summary')}</h3>
  <p><strong>Status:</strong> ${report?.fitSummary?.overallStatus || ''}</p>
  <p><strong>Critical Areas:</strong> ${(report?.fitSummary?.criticalAreas || []).length > 0 ? report.fitSummary.criticalAreas.join(', ') : 'None'}</p>
</div>
        `;

        handleShareContent({
            type: 'color_analysis',
            title: `${t('style_report_title')} - ${new Date().toLocaleDateString()}`,
            content: content,
            sourceImageUrl: imageUrl
        });
        Logger.info(`Style report saved for user ${currentUserId}`);
    };

    const handleGetStyleTips = async () => {
        const hasItems = currentLayer.garments.length > 0 || currentLayer.accessories.length > 0;
        if (!hasItems) return;

        setIsLoading(true);
        setStyleTipsContent(null);
        setShowStyleTipsModal(true);
        setStyleTipsType('tips');
        try {
            const garments = currentLayer.garments.map(g => `${t(g.nameKey)} (${currentLayer.appliedGarmentColors[g.id] || 'default'})`).join(', ');
            const accessories = currentLayer.accessories.map(a => `${t(a.nameKey)} (${currentLayer.appliedAccessoryColors[a.id] || 'default'})`).join(', ');

            const context = `Wearing: ${garments}. Accessories: ${accessories}. Style Persona: ${userProfile.stylePersona || 'None'}. Season: ${userProfile.colorSeason || 'Unknown'}.`;
            const tips = await getFashionAdvice("Give me styling tips for this outfit. Suggest accessories, layering, and occasion suitability.", context, (lang as any), userProfile);
            setStyleTipsContent(tips);
        } catch (e) {
            setError(t('error_generic_short'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreativeEdit = async (type: 'ageing' | 'wrinkles' | 'golden_hour' | 'remove_glasses' | 'remove_hat' | 'remove_watch' | 'remove_bag') => {
        if (!baseModelImageUrl) return;
        setIsLoading(true);
        setLoadingAction('creative');

        const loadingMsgs = {
            ageing: t('loading_ageing_correction'),
            wrinkles: t('loading_wrinkle_removal'),
            golden_hour: t('loading_golden_hour'),
            remove_glasses: t('loading_remove_glasses'),
            remove_hat: t('loading_remove_hat'),
            remove_watch: t('loading_remove_watch'),
            remove_bag: t('loading_remove_bag')
        };

        setLoadingMessage(loadingMsgs[type]);
        setError(null);

        try {
            const currentPose = currentLayer.currentPoseInstruction;
            const sourceImage = currentLayer.poseOutputs[currentPose] || baseModelImageUrl;

            let prompt = "";
            if (type === 'ageing') {
                prompt = "Apply face ageing correction to this person. Subtly rejuvenate the skin, improve elasticity and radiance while maintaining the natural facial features and identity.";
            } else if (type === 'wrinkles') {
                prompt = "Remove wrinkles and fine lines from the person's face. Smoothen the skin texture naturally without making it look artificial or 'plastic'. Maintain facial structure.";
            } else if (type === 'golden_hour') {
                prompt = "Apply a 'Golden Hour' lighting effect to this fashion image. Warm, soft, cinematic sunlight coming from the side. Enhance skin glow and fabric texture. Create a high-end editorial look while maintaining the person's identity.";
            } else if (type === 'remove_glasses') {
                prompt = "Remove the glasses from the person's face. Reconstruct the eyes and surrounding skin naturally. Maintain facial structure and person's identity.";
            } else if (type === 'remove_hat') {
                prompt = "Remove the hat or headwear. Reconstruct the hair and forehead naturally. Maintain facial structure and person's identity.";
            } else if (type === 'remove_watch') {
                prompt = "Remove the watch or wrist accessory. Reconstruct the wrist and arm skin naturally. Maintain skin texture.";
            } else if (type === 'remove_bag') {
                prompt = "Remove the bag or handbag the person is carrying. Reconstruct the body and background behind the bag naturally. Ensure the outcome is seamless.";
            }

            const resultImage = await performImageEdit(sourceImage, prompt, userProfile.saveImageQuality);

            const newLayer = {
                ...currentLayer,
                poseOutputs: { ...currentLayer.poseOutputs, [currentPose]: resultImage }
            };
            updateLayerWithHistory(newLayer);

            const updatedProfile = await incrementGenerationCount(currentUserId);
            setUserProfile(updatedProfile);

            Logger.info(`Creative edit applied: ${type}`);
        } catch (err) {
            const details = err instanceof Error ? err.message : String(err);
            Logger.error(`Creative edit failed (${type})`, err);
            setError(t('error_creative_edit', { details }));
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
            setLoadingAction(null);
        }
    };

    const handleGenerateAttitude = async () => {
        setIsLoading(true);
        setLoadingAction('creative');
        setLoadingMessage(t('loading_generating_attitude', 'Generating coaching note...'));
        setError(null);
        try {
            const garments = currentLayer.garments.map(g => `${t(g.nameKey)} (${currentLayer.appliedGarmentColors[g.id] || 'default'})`).join(', ');
            const accessories = currentLayer.accessories.map(a => `${t(a.nameKey)} (${currentLayer.appliedAccessoryColors[a.id] || 'default'})`).join(', ');
            const outfitContext = `Wearing: ${garments}. Accessories: ${accessories}.`;

            const note = await generateAttitudeNote(outfitContext, userProfile, (lang as any));

            const newLayer: OutfitLayer = {
                ...currentLayer,
                attitudeNote: note.attitudeNote,
                postureGuidance: note.postureGuidance
            };
            updateLayerWithHistory(newLayer);
            Logger.info('Attitude Note generated successfully.');
        } catch (e) {
            Logger.error('Failed to generate attitude note', e);
            setError(t('error_generic_short'));
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
            setLoadingAction(null);
        }
    };

    // Unified Studio Context Helper
    const isStudioRoute = ['canvas', 'old-canvas', 'style-lab', 'studio', 'try-on', 'wardrobe'].includes(currentPage);

    return (
        <div className={`min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-sans transition-colors duration-300 flex flex-col overflow-hidden ${isDarkMode ? 'dark' : ''}`}>
            <NavBar
                onStartOver={() => {
                    resetOutfit();
                    setBaseModelImageUrl(null);
                    setShowStartScreen(false);
                    setCurrentOutfitId(null);
                    handleNavigate('home');
                }}
                isLoading={isLoading}
                onSaveOutfit={() => setShowSaveModal(true)}
                onShareOutfit={() => setShowShareModal(true)}
                onShowLookbookCreator={() => setShowLookbookModal(true)}
                onFindSimilarItems={() => (currentLayer?.garments.length > 0) && handleFindSimilar(currentLayer.garments[0])}
                onGetStyleTips={handleGetStyleTips}
                isDarkMode={isDarkMode}
                onToggleDarkMode={() => setIsDarkMode(!isDarkMode)}
                onShowBasket={() => setShowBasketModal(true)}
                onShowAgentChat={() => handleNavigate('styling-advisor', { section: 'shopping_assistant' })}
                onToggleLeftPanel={() => setIsLeftPanelOpen(!isLeftPanelOpen)}
                isLeftPanelOpen={isLeftPanelOpen}
                onToggleRightPanel={() => setIsRightPanelOpen(!isRightPanelOpen)}
                isRightPanelOpen={isRightPanelOpen}
                basketItemCount={basket.length}
                handleUserChangeRequest={handleUserChangeRequest}
                isStudioPage={isStudioRoute}
            />

            <AnimatePresence>
            </AnimatePresence>

            <main className="flex-grow flex overflow-hidden relative">
                <AnimatePresence>
                    {(isStudioRoute && isLeftPanelOpen) && (
                        <motion.div
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 350, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            className="h-full border-r border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md overflow-hidden flex-shrink-0 z-10"
                        >
                            <div id="onboarding-wardrobe-panel" className="w-[350px] h-full p-4">
                                <WardrobePanel
                                    onItemSelect={handleItemSelect}
                                    onFindSimilar={handleFindSimilar}
                                    activeItemIds={[...(currentLayer?.garments?.map(g => g.id) || []), ...(currentLayer?.accessories?.map(a => a.id) || [])].filter(Boolean) as string[]}
                                    isLoading={isLoading}
                                    wardrobe={wardrobe}
                                    onApplyAccessorySet={handleApplyAccessorySet}
                                    predefinedAccessorySets={[...predefinedAccessorySets, ...adminAccessorySets]}
                                    userProfile={userProfile}
                                    onAddItem={(item) => setWardrobe([item, ...wardrobe])}
                                    currentUserId={currentUserId}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className={`flex-grow h-full ${isStudioRoute ? 'overflow-hidden' : 'overflow-y-auto'} overflow-x-hidden relative`}>
                    <AnimatePresence mode='wait'>
                        <AppRoutes
                            location={location}
                            key={location.pathname}
                            onPhotoUpload={handlePhotoUpload}
                            onGenerateModel={() => setShowStartScreen(true)}
                            baseModelImageUrl={baseModelImageUrl}
                            handleNavigate={handleNavigate}
                            handleModelFinalized={handleModelFinalized}
                            currentLayer={currentLayer}
                            isLoading={isLoading}
                            loadingMessage={loadingMessage}
                            loadingAction={loadingAction}
                            error={error}
                            onClearError={() => setError(null)}
                            handlePoseSelect={handlePoseSelect}
                            POSE_CATEGORIES={POSE_CATEGORIES}
                            onShopTheLook={() => currentLayer?.mainGarment && handleFindSimilar(currentLayer.mainGarment)}
                            currentOutfitId={currentOutfitId}
                            onSaveContent={handleShareContent}
                            onSaveReport={handleSaveStyleReport}
                            styleReport={styleReport}
                            onCloseReport={() => setStyleReport(null)}
                            onGenerateReport={handleAnalyzeOutfit}
                            onSaveOutfit={() => setShowSaveModal(true)}
                            onStartOver={() => {
                                resetOutfit();
                                setShowStartScreen(false);
                                handleNavigate('home');
                            }}
                            handleSceneUpdate={handleSceneUpdate}
                            handleGenerate3DViews={handleGenerate3DViews}
                            handleToggleFavoritePose={handleToggleFavoritePose}
                            onDeleteContent={async (id: string) => {
                                const updatedContent = savedContent.filter(c => c.id !== id);
                                setSavedContent(updatedContent);
                                await saveUserSavedContent(currentUserId, updatedContent);
                            }}
                            onLoadOutfit={(outfit: any) => {
                                updateLayerWithHistory(outfit.layer);
                                setBaseModelImageUrl(outfit.baseModelImageUrl);
                                setCurrentOutfitId(outfit.id);
                                handleNavigate('canvas');
                            }}
                            onDeleteOutfit={async (id: string) => {
                                const updatedOutfits = savedOutfits.filter(o => o.id !== id);
                                setSavedOutfits(updatedOutfits);
                                await saveUserSavedOutfits(currentUserId, updatedOutfits);
                            }}
                            onShowLookbookCreator={() => setShowLookbookModal(true)}
                            onCreativeEdit={handleCreativeEdit}
                            handleShareContent={handleShareContent}
                            pageParams={pageParams}
                            onRestartOnboarding={handleRestartOnboarding}
                            allUsers={allUsers}
                            onCreateNewUser={(name: string) => {
                                const newId = `${name.toLowerCase().replace(/\s+/g, '')}-user`;
                                if (!allUsers.includes(newId)) setAllUsers([...allUsers, newId]);
                            }}
                            onDeleteUser={(userId: string) => {
                                setAllUsers(allUsers.filter(u => u !== userId));
                                if (currentUserId === userId) setCurrentUserId('guest-user');
                            }}
                            onChangeUser={handleUserChangeRequest}
                            onOpenVoucherModal={() => setShowVoucherModal(true)}
                            onOpenAuthModal={() => setIsAuthModalOpen(true)}
                            wardrobe={wardrobe}
                            setWardrobe={setWardrobe}
                            onItemAdd={moodboardInjectionItem}
                        />
                    </AnimatePresence>
                </div>

                <AnimatePresence>
                    {(isStudioRoute && isRightPanelOpen && pageParams.mode !== 'moodboard') && (
                        <motion.div
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 320, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            className="h-full border-l border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md overflow-hidden flex-shrink-0 z-10"
                        >
                            <div id="onboarding-outfit-stack" className="w-[320px] h-full p-4">
                                <OutfitStack
                                    currentLayer={currentLayer}
                                    onRemoveGarment={handleRemoveGarment}
                                    onRemoveAccessory={(id) => {
                                        const newAccessories = currentLayer.accessories.filter(a => a.id !== id);
                                        const newLayer = { ...currentLayer, accessories: newAccessories };
                                        delete newLayer.appliedAccessoryColors[id];
                                        updateLayerWithHistory(newLayer);
                                        triggerGeneration(newLayer, 'item');
                                    }}
                                    onSetColorPickerItem={(item) => {
                                        setColorPickerTarget({ type: item.type as any, id: item.id });
                                        setShowColorPicker(true);
                                    }}
                                    onUndo={handleUndo}
                                    onRedo={handleRedo}
                                    canUndo={true}
                                    canRedo={true}
                                    onReorderAccessory={() => { }}
                                    onFindSimilar={() => currentLayer?.mainGarment && handleFindSimilar(currentLayer.mainGarment)}
                                    onApplyAccessorySet={handleApplyAccessorySet}
                                    predefinedAccessorySets={[...predefinedAccessorySets, ...adminAccessorySets]}
                                    onGetStyleTips={handleGetStyleTips}
                                    onAnalyzeOutfit={handleAnalyzeOutfit}
                                    onAnalyzeColor={handleAnalyzeColor}
                                    onSuggestVariations={handleSuggestVariations}
                                    onSaveOutfit={() => setShowSaveModal(true)}
                                    onNavigate={handleNavigate}
                                    userProfile={userProfile}
                                    isLoading={isLoading}
                                    isModelLoaded={!!baseModelImageUrl}
                                    onAddToLookbook={() => setShowLookbookModal(true)}
                                    loadedOutfitId={currentOutfitId}
                                    isGuest={currentUserId.includes('guest')}
                                    onGenerateAttitude={handleGenerateAttitude}
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            <AnimatePresence>
                {showStartScreen && userImageFile && (
                    <ModelGenerationScreen
                        userImageFile={userImageFile}
                        onModelFinalized={handleModelFinalized}
                        onCancel={() => setShowStartScreen(false)}
                        isDarkMode={isDarkMode}
                        onNavigate={handleNavigate}
                        currentUserId={currentUserId}
                    />
                )}
            </AnimatePresence>

            {showOnboarding && onboardingStep > 0 && currentPage === 'canvas' && (
                <OnboardingTooltip
                    step={onboardingStep}
                    totalSteps={4}
                    onNext={handleOnboardingNext}
                    onSkip={handleOnboardingSkip}
                    title={getOnboardingStepData(onboardingStep).title}
                    content={getOnboardingStepData(onboardingStep).content}
                    targetId={getOnboardingStepData(onboardingStep).id}
                />
            )}

            <AuthModal
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
            />

            <OutfitNameInputModal
                isOpen={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                onSave={handleSaveOutfit}
                isLoading={isLoading}
                previewImageUrl={currentLayer?.poseOutputs?.[currentLayer?.currentPoseInstruction] || baseModelImageUrl || ''}
                initialName={currentOutfitId ? savedOutfits.find(o => o.id === currentOutfitId)?.name : ''}
                initialDescription={currentOutfitId ? savedOutfits.find(o => o.id === currentOutfitId)?.description : ''}
                initialTags={currentOutfitId ? savedOutfits.find(o => o.id === currentOutfitId)?.tags : []}
                poseDescription={t(POSE_CATEGORIES.flatMap((c: any) => c.poses).find((p: any) => p.instruction === currentLayer?.currentPoseInstruction)?.labelKey as any)}
                backgroundDescription={currentLayer.sceneConfig?.backgroundPrompt !== 'neutral light gray studio backdrop' ? currentLayer.sceneConfig?.backgroundPrompt : undefined}
                currentLayer={currentLayer}
            />

            <ShareOutfitModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                currentOutfitImageUrl={currentLayer?.poseOutputs?.[currentLayer?.currentPoseInstruction] || baseModelImageUrl || undefined}
                isLoading={isLoading}
                onSaveContent={handleShareContent}
                currentLayer={currentLayer}
                baseModelImageUrl={baseModelImageUrl}
                userProfile={userProfile}
            />

            <LookbookCreatorModal
                isOpen={showLookbookModal}
                onClose={() => setShowLookbookModal(false)}
                savedOutfits={savedOutfits}
                isLoadingApp={isLoading}
                onSaveLookbook={(lookbook) => {
                    const newLookbook = { ...lookbook, id: `lookbook-${Date.now()}`, createdAt: new Date().toISOString() };
                    const updated = [...lookbooks, newLookbook];
                    setLookbooks(updated);
                    saveUserLookbooks(currentUserId, updated);
                    setShowLookbookModal(false);
                }}
                initialSelectedOutfitId={currentOutfitId}
                userProfile={userProfile}
            />



            <StyleTipsModal
                isOpen={showStyleTipsModal}
                onClose={() => setShowStyleTipsModal(false)}
                content={styleTipsContent || outfitVariations}
                isLoading={isLoading}
                error={error}
                onRegenerate={styleTipsType === 'tips' ? handleGetStyleTips : handleSuggestVariations}
                onSaveContent={handleShareContent}
                title={styleTipsType === 'tips' ? t('nav_getStyleTip') : styleTipsType === 'color_analysis' ? t('myStylesPage_analyzeOutfit') : t('variationsModal_title')}
                saveType={styleTipsType}
            />

            <OutfitVariationsModal
                isOpen={showVariationsModal}
                onClose={() => setShowVariationsModal(false)}
                variations={outfitVariations}
                isLoading={isLoading}
                error={error}
                onRegenerate={handleSuggestVariations}
            />

            <ShoppingBasketModal
                isOpen={showBasketModal}
                onClose={() => setShowBasketModal(false)}
                items={basket}
                onRemoveItem={(url) => setBasket(prev => prev.filter(i => i.productUrl !== url))}
            />

            <AnimatePresence>
                {showColorPicker && (
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 p-4 z-50 border-t border-gray-200 dark:border-gray-700 shadow-[0_-5px_20px_rgba(0,0,0,0.1)]"
                    >
                        <div className="max-w-2xl mx-auto">
                            <ColorPickerPanel
                                onColorChange={handleColorChange}
                                currentColor={colorPickerTarget?.type === 'garment' ? (currentLayer.appliedMainGarmentColor || '#ffffff') : (colorPickerTarget ? currentLayer.appliedAccessoryColors[colorPickerTarget.id] || '#ffffff' : '#ffffff')}
                                disabled={isLoading}
                                isVisible={true}
                                title={colorPickerTarget ? t('outfitStack_changeColor_aria', { itemName: colorPickerTarget.type }) : t('canvas_colorPicker_title')}
                                onClose={() => setShowColorPicker(false)}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <VoucherPurchaseModal
                isOpen={showVoucherModal}
                onClose={() => setShowVoucherModal(false)}
                onPurchase={submitVoucherPurchase}
                services={portfolioItems}
            />



            <TrendSpotter
                isOpen={showTrendSpotter}
                setIsOpen={setShowTrendSpotter}
            />

            <PasswordPromptModal
                isOpen={showPasswordModal}
                onClose={() => { setShowPasswordModal(false); setPendingUserId(null); }}
                onSubmit={handlePasswordSubmit}
                username={pendingUserId?.split('-')[0] || 'User'}
            />

            <AgentingWorkflowChat
                isOpen={showAgentChat}
                onClose={() => setShowAgentChat(false)}
                userProfile={userProfile}
                setUserProfile={setUserProfile}
                findSimilarItems={async (item: { name: string, url: string }) => {
                    const { results } = await findSimilarItems(item, undefined, userProfile);
                    return { results };
                }}
                addToBasket={(item: RetailItem) => setBasket(prev => [...prev, item])}
                submitBooking={async (details: BookingDetails) => {
                    const res = await submitBooking(details);
                    return { confirmation: res.confirmation, date: res.date };
                }}
                savedOutfits={savedOutfits}
                baseModelImageUrl={baseModelImageUrl}
                onSaveContent={handleShareContent}
                onSaveLookbook={(lookbook: Omit<Lookbook, 'id' | 'createdAt'>) => {
                    const newLookbook = { ...lookbook, id: `lookbook-${Date.now()}`, createdAt: new Date().toISOString() };
                    const updated = [...lookbooks, newLookbook];
                    setLookbooks(updated);
                    saveUserLookbooks(currentUserId, updated);
                    setShowLookbookModal(false);
                }}
                currentLayer={currentLayer}
                onOpenColorPicker={() => {
                    setShowColorPicker(true);
                    return true;
                }}
            />
        </div>
    );
};

export default App;
